import React, { useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const TAKEOUT_LEVELS = [30, 40, 50];

const headerSx = {
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#006842",
  whiteSpace: "normal",
  py: 0.75,
  px: 1,
  borderBottom: "2px solid #006842",
};

const cellSx = {
  fontSize: "0.78rem",
  py: 0.5,
  px: 1,
  borderBottom: "1px solid",
  borderColor: "grey.100",
};

function formatTakeoutPnl(value) {
  if (value == null || isNaN(value)) return "—";
  const formatted = Math.abs(Math.round(value)).toLocaleString("en-US");
  return value < 0 ? `(${formatted})` : formatted;
}

function formatPrice(value) {
  if (value == null || isNaN(value)) return null;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatQuantity(value) {
  if (value == null || isNaN(value)) return null;
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function pnlColor(value) {
  if (value == null || isNaN(value) || value >= 0) return "inherit";
  return "#c53030";
}

function compareValues(a, b, key, direction) {
  const av = a?.[key];
  const bv = b?.[key];
  const aMissing = av == null || av === "";
  const bMissing = bv == null || bv === "";
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof av === "string" || typeof bv === "string") {
    const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
    return direction === "asc" ? cmp : -cmp;
  }
  const cmp = av - bv;
  return direction === "asc" ? cmp : -cmp;
}

export default function ConvertTakeoutRiskPanel({ data, loading }) {
  const [open, setOpen] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "strategy", direction: "asc" });

  const rows = data?.data || [];
  const takeoutDate =
    data?.takeout_date ||
    data?.takeoutDate ||
    rows.find((row) => row?.takeout_date || row?.takeoutDate)?.takeout_date ||
    rows.find((row) => row?.takeoutDate)?.takeoutDate ||
    null;

  if (process.env.NODE_ENV !== "production" && data && !takeoutDate) {
    // eslint-disable-next-line no-console
    console.debug("ConvertTakeoutRiskPanel: no takeout_date field found in response", data);
  }

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => compareValues(a, b, sortConfig.key, sortConfig.direction));
    return copy;
  }, [rows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "strategy" ? "asc" : "desc" },
    );
  };

  return (
    <Box
      id="convert-takeout-risk"
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 1,
        p: { xs: 1, sm: 2 },
        bgcolor: "background.paper",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          mb: open ? 1 : 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "#BE9B53" }}>
            Convert Takeout Risk
          </Typography>
          {takeoutDate && (
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary" }}>
              Takeout Date: {takeoutDate}
            </Typography>
          )}
        </Box>
        <ExpandMoreIcon
          sx={{
            color: "#BE9B53",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        />
      </Box>

      <Collapse in={open} unmountOnExit>
        {loading ? (
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
        ) : sortedRows.length === 0 ? (
          <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            No convert takeout risk data available for this date.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 480, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerSx, minWidth: 220 }}>
                    <TableSortLabel
                      active={sortConfig.key === "strategy"}
                      direction={sortConfig.key === "strategy" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("strategy")}
                    >
                      Strategy
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...headerSx, textAlign: "right", minWidth: 100 }}>
                    <TableSortLabel
                      active={sortConfig.key === "quantity"}
                      direction={sortConfig.key === "quantity" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("quantity")}
                      sx={{ justifyContent: "flex-end", width: "100%" }}
                    >
                      Convert Qty
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...headerSx, textAlign: "right", minWidth: 100 }}>
                    <TableSortLabel
                      active={sortConfig.key === "hedge_quantity"}
                      direction={sortConfig.key === "hedge_quantity" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("hedge_quantity")}
                      sx={{ justifyContent: "flex-end", width: "100%" }}
                    >
                      Stock Qty
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...headerSx, textAlign: "right", minWidth: 100 }}>
                    <TableSortLabel
                      active={sortConfig.key === "initial_convert_price"}
                      direction={sortConfig.key === "initial_convert_price" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("initial_convert_price")}
                      sx={{ justifyContent: "flex-end", width: "100%" }}
                    >
                      Initial Convert Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...headerSx, textAlign: "right", minWidth: 100 }}>
                    <TableSortLabel
                      active={sortConfig.key === "initial_stock_price"}
                      direction={sortConfig.key === "initial_stock_price" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("initial_stock_price")}
                      sx={{ justifyContent: "flex-end", width: "100%" }}
                    >
                      Initial Stock Price
                    </TableSortLabel>
                  </TableCell>
                  {TAKEOUT_LEVELS.map((pct) => (
                    <TableCell key={pct} sx={{ ...headerSx, textAlign: "right", minWidth: 130 }}>
                      <TableSortLabel
                        active={sortConfig.key === `pnl_${pct}`}
                        direction={sortConfig.key === `pnl_${pct}` ? sortConfig.direction : "asc"}
                        onClick={() => handleSort(`pnl_${pct}`)}
                        sx={{ justifyContent: "flex-end", width: "100%" }}
                      >
                        {pct}% take out PnL
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => {
                  const qtyLabel = formatQuantity(row.quantity);
                  const hedgeQtyLabel = formatQuantity(row.hedge_quantity);
                  const initialConvertPriceLabel = formatPrice(row.initial_convert_price);
                  const initialStockPriceLabel = formatPrice(row.initial_stock_price);
                  return (
                    <TableRow key={row.strategy} hover>
                      <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{row.strategy}</TableCell>
                      <TableCell sx={{ ...cellSx, textAlign: "right", fontFamily: "monospace" }}>
                        {qtyLabel ?? "—"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, textAlign: "right", fontFamily: "monospace" }}>
                        {hedgeQtyLabel ?? "—"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, textAlign: "right", fontFamily: "monospace" }}>
                        {initialConvertPriceLabel ?? "—"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, textAlign: "right", fontFamily: "monospace" }}>
                        {initialStockPriceLabel ?? "—"}
                      </TableCell>
                      {TAKEOUT_LEVELS.map((pct) => {
                        const pnl = row[`pnl_${pct}`];
                        const price = formatPrice(row[`price_${pct}`]);
                        const stockPrice = formatPrice(row[`stock_price_${pct}`]);
                        return (
                          <TableCell key={pct} sx={{ ...cellSx, textAlign: "right" }}>
                            <Typography
                              sx={{
                                fontSize: "0.76rem",
                                fontWeight: 600,
                                color: pnlColor(pnl),
                                fontFamily: "monospace",
                                lineHeight: 1.15,
                              }}
                            >
                              {formatTakeoutPnl(pnl)}
                            </Typography>
                            {price && (
                              <Typography sx={{ fontSize: "0.62rem", color: "text.secondary", fontFamily: "monospace", lineHeight: 1.1, mt: 0.1 }}>
                                Cvt {price}
                              </Typography>
                            )}
                            {stockPrice && (
                              <Typography sx={{ fontSize: "0.62rem", color: "text.secondary", fontFamily: "monospace", lineHeight: 1.1 }}>
                                Stk {stockPrice}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Box>
  );
}
